import React, { useEffect, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import { DndContext } from "@dnd-kit/core";
import { SortableContext, verticalListSortingStrategy } from "@dnd-kit/sortable";
import ItemCard from "~/pages/CharacterSheet/widgets/ItemCard/ItemCard";
import { CharacterContextProvider, type CharacterActions } from "~/contexts/CharacterContext";
import type { CharacterData } from "~/types/storage";
import type { ItemOrAbility } from "~/pages/CharacterSheet/types";
import styles from "~/widgets/ItemDetailsTooltip/ItemDetailsTooltip.module.scss";

interface TooltipPosition {
  top: number;
  left: number;
  maxHeight: number;
}

export interface ItemDetailsTooltipProps {
  item: { id: string; content: string } | null;
  isEditing: boolean;
  anchorRect: DOMRect | null;
  onClose: () => void;
  onToggleEdit: (id: string) => void;
  onUpdate?: (id: string, content: string) => void;
  onDelete?: (id: string) => void;
  characterData?: CharacterData | null;
  actions?: CharacterActions;
}

/**
 * Calculates optimal tooltip position based on anchor element and viewport.
 * Prefers left-aligned positioning on small screens to keep content visible.
 */
function calculatePosition(anchorRect: DOMRect): TooltipPosition {
  const padding = 12;
  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;

  // Use smaller width on mobile
  const isSmallScreen = viewportWidth < 480;
  const tooltipWidth = isSmallScreen ? Math.min(320, viewportWidth - padding * 2) : 420;

  let top: number;
  let left: number;

  // On small screens, always align to left edge with padding
  if (isSmallScreen) {
    left = padding;
  } else {
    // Calculate available space
    const spaceRight = viewportWidth - anchorRect.left - padding;
    const spaceLeft = anchorRect.right - padding;

    if (spaceRight >= tooltipWidth) {
      left = anchorRect.left;
    } else if (spaceLeft >= tooltipWidth) {
      left = anchorRect.right - tooltipWidth;
    } else {
      // Align to left with padding as fallback
      left = padding;
    }
  }

  // Vertical positioning
  const spaceBelow = viewportHeight - anchorRect.bottom - padding;
  const spaceAbove = anchorRect.top - padding;
  const estimatedHeight = Math.min(300, viewportHeight * 0.4);

  if (spaceBelow >= estimatedHeight) {
    top = anchorRect.bottom + padding;
  } else if (spaceAbove >= estimatedHeight) {
    top = anchorRect.top - estimatedHeight - padding;
  } else {
    top = spaceBelow > spaceAbove
      ? anchorRect.bottom + padding
      : Math.max(padding, anchorRect.top - estimatedHeight - padding);
  }

  // Final boundary checks
  left = Math.max(padding, Math.min(left, viewportWidth - tooltipWidth - padding));
  top = Math.max(padding, top);

  const maxHeight = viewportHeight - top - padding;

  return { top, left, maxHeight };
}

/**
 * ItemDetailsTooltip Component
 *
 * A positioned tooltip that displays full item details using ItemCard.
 * Handles viewport-aware positioning with small screen optimization.
 */
const ItemDetailsTooltip: React.FC<ItemDetailsTooltipProps> = ({
  item,
  isEditing,
  anchorRect,
  onClose,
  onToggleEdit,
  onUpdate,
  onDelete,
  characterData,
  actions,
}) => {
  const [position, setPosition] = useState<TooltipPosition>({ top: 0, left: 0, maxHeight: 400 });

  // Calculate position when anchor changes
  useEffect(() => {
    if (anchorRect) {
      setPosition(calculatePosition(anchorRect));
    }
  }, [anchorRect]);

  // Handle Escape key
  useEffect(() => {
    if (!item) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [item, onClose]);

  const handleUpdate = useCallback((id: string, content: string) => {
    onUpdate?.(id, content);
  }, [onUpdate]);

  const handleDelete = useCallback((id: string) => {
    onDelete?.(id);
    onClose();
  }, [onDelete, onClose]);

  // No-op for move since we don't support reordering in popup
  const handleMove = useCallback(() => { }, []);

  if (!item || !anchorRect) {
    return null;
  }

  const itemWithEditState: ItemOrAbility = {
    ...item,
    isEditing,
  };

  const tooltipContent = (
    <>
      <div className={styles.overlay} onClick={onClose} />
      <div
        className={styles.tooltip}
        style={{
          top: position.top,
          left: position.left,
          maxHeight: position.maxHeight,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <DndContext>
          <SortableContext items={[itemWithEditState]} strategy={verticalListSortingStrategy}>
            <ItemCard
              item={itemWithEditState}
              onToggleEdit={onToggleEdit}
              onUpdate={handleUpdate}
              onDelete={handleDelete}
              onMove={handleMove}
              disableDrag
            />
          </SortableContext>
        </DndContext>
      </div>
    </>
  );

  // Wrap with character context if data is provided
  if (characterData && actions) {
    return createPortal(
      <CharacterContextProvider characterData={characterData} actions={actions}>
        {tooltipContent}
      </CharacterContextProvider>,
      document.body
    );
  }

  return createPortal(tooltipContent, document.body);
};

export default ItemDetailsTooltip;
